import { useCallback } from 'react';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import { getUnsavedChangesFunctionsForArea, UnsavedChangesArea } from '@hooks-clean/unsaved-changes/shared';

export type UseUnsavedChangesConfirmationInnerProps = {
    hasChangesFn: () => boolean;
    clearChangesFn: () => void;
    confirmationFn: () => Promise<boolean>;
};

/**
 * **Do not use this - use `useUnsavedChangesConfirmation` instead**
 *
 * **This exists for unit-testing purposes**
 */
export function useUnsavedChangesConfirmationInner({
    hasChangesFn,
    clearChangesFn,
    confirmationFn,
}: UseUnsavedChangesConfirmationInnerProps) {
    return useCallback(async () => {
        const hasChanges = hasChangesFn();
        // If no changes, return true immediately
        if (!hasChanges) {
            return true;
        }

        // Otherwise, show confirmation
        const confirmed = await confirmationFn();

        // If confirmation confirmed, clear unsaved changes
        if (confirmed) {
            clearChangesFn();
        }

        return confirmed;
    }, [hasChangesFn, confirmationFn, clearChangesFn]);
}

export type UseUnsavedChangesConfirmationProps = { area: UnsavedChangesArea } & (
    | {
          mode: 'any-component';
          componentNames?: never; // Cannot specify component(s) for this variant
      }
    | {
          mode: 'individual-components';
          // If string[] is used, this should have a stable reference
          componentNames: string | string[];
      }
);

/**
 * Creates a confirmation function for confirming whether the user wants to discard unsaved changes.
 *
 * If `mode` is `"any-component"`, this will look for changes to any component in the appropriate (based on `area`) unsaved-changes tracker.
 *
 * If `mode` is `"individual-components"`, this will look for changes to the specified component(s) (based on `componentNames`)
 *   in the appropriate (based on `area`) unsaved-changes tracker.
 *
 * When called:
 *
 * - if no changes are detected, true is returned immediately
 * - if changes are detected, a confirmation is shown, and the result returned.
 *   - if the confirmation is confirmed, the changes are cleared.
 *
 * **!!! If `componentNames` is an array, it must have a stable reference (e.g. defined outside of React, or memoized).**
 */
export function useUnsavedChangesConfirmation({ area, mode, componentNames }: UseUnsavedChangesConfirmationProps) {
    const context = useUnsavedChangesContext();

    const { hasChanges, hasAnyChanges, setChanges, clearAllChanges } = getUnsavedChangesFunctionsForArea(context, area);

    const hasChangesFn = useCallback(() => {
        // If mode is for any-component, return whether any component has changes
        if (mode === 'any-component') {
            return hasAnyChanges();
        }

        // Otherwise, check whether the specified component(s) have changes
        // If `componentNames` is a string, make it a 1-element array
        const resolvedComponentNames = Array.isArray(componentNames) ? componentNames : [componentNames];

        return resolvedComponentNames.some((n) => hasChanges(n));
    }, [mode, hasAnyChanges, componentNames, hasChanges]);

    const clearChangesFn = useCallback(() => {
        // If is for any-component, clear all changes
        if (mode === 'any-component') {
            clearAllChanges();
            return;
        }

        // Otherwise, clear the specified component(s)
        // If `componentNames` is a string, make it a 1-element array
        const resolvedComponentNames = Array.isArray(componentNames) ? componentNames : [componentNames];

        resolvedComponentNames.forEach((n) => setChanges(n, false));
    }, [mode, clearAllChanges, componentNames, setChanges]);

    return useUnsavedChangesConfirmationInner({
        hasChangesFn,
        clearChangesFn,
        confirmationFn: context.confirmDiscardChanges,
    });
}
